/**
 * App-level provider composition for the admin SPA. Wraps the tree
 * with the theme provider, the admin API bag, and the auth provider.
 *
 * The HTTP client is created once at module scope (same pattern as
 * the dashboard) so the `on_unauthorized` callback can reach into a
 * module-scope hook variable that's updated when the AuthProvider
 * mounts.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { createAdminClient } from '../api/admin-client.js';
import type { AdminClient } from '../api/admin-client.js';
import { AdminApiProvider } from '../api/api-context.js';
import { createHttpClient } from '../api/http-client.js';
import { API_ORIGIN } from './env.js';
import { AuthProvider } from './auth-context.js';
import type { SetView } from './view-router.js';
import { ThemeProvider } from '../lib/theme.js';

let onUnauthorizedHook: (() => void) | null = null;

const httpClient = createHttpClient({
  api_origin: API_ORIGIN,
  on_unauthorized: () => {
    if (onUnauthorizedHook !== null) {
      onUnauthorizedHook();
    }
  },
});

const adminClient: AdminClient = createAdminClient({ http: httpClient });

export interface AppProvidersProps {
  children: ReactNode;
  setView: SetView;
  /**
   * Forwarded to `<AuthProvider />`. `<App />` sets it to `true` when
   * the SPA starts on the `'verify'` view (a `?token=…` magic-link
   * callback), so the provider's mount-time GET /me doesn't race the
   * token-consume → cookie-set → refresh chain `VerifyView` owns.
   */
  skipAuthBootstrap?: boolean;
}

export function AppProviders({
  children,
  setView,
  skipAuthBootstrap,
}: AppProvidersProps): JSX.Element {
  // Wire the on_unauthorized hook to the current `setView`. The
  // module-scope variable lets the HTTP client (itself module-scope)
  // reach into the latest mounted callback without re-instantiation
  // per render.
  useEffect(() => {
    onUnauthorizedHook = (): void => {
      setView('login');
    };
    return (): void => {
      onUnauthorizedHook = null;
    };
  }, [setView]);

  // `exactOptionalPropertyTypes` rejects `skipBootstrap={undefined}` —
  // build a conditional prop bag so we only pass it when it's `true`.
  const authExtra = skipAuthBootstrap === true ? { skipBootstrap: true as const } : {};

  return (
    <ThemeProvider>
      <AdminApiProvider api={adminClient}>
        <AuthProvider setView={setView} {...authExtra}>
          {children}
        </AuthProvider>
      </AdminApiProvider>
    </ThemeProvider>
  );
}
